import React, { useEffect, useState } from 'react';
import {
  Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, TextField, Grid,
  Button, Pagination, Box, IconButton, Tooltip, FormControl,
  InputLabel, Select, MenuItem, Chip, OutlinedInput, TableSortLabel,
} from '@mui/material';
import { Search, Edit, Delete, Visibility, Add, CheckCircle, Cancel, Warning } from '@mui/icons-material';
import axios from '../utils/api';
import { US_STATES } from '../utils/constants';

type RowType = 'directory' | 'referral' | 'agency';

interface RegistrationRow {
  id: string;
  type: RowType;
  displayName: string;
  organization: string;
  email: string;
  city: string | null;
  state: string | null;
  activeStates: string;
  emailVerified: boolean;
  duplicateFlagged: boolean;
  createdAt: string;
}

const TYPE_OPTIONS: { value: RowType; label: string }[] = [
  { value: 'directory', label: 'Directory Lawyer' },
  { value: 'referral', label: 'Referral Lawyer' },
  { value: 'agency', label: 'Agency' },
];

const TYPE_LABELS: Record<RowType, string> = {
  directory: 'Directory',
  referral: 'Referral',
  agency: 'Agency',
};

const typeChipColor = (type: RowType): 'primary' | 'default' | 'secondary' => {
  if (type === 'agency') return 'secondary';
  if (type === 'referral') return 'default';
  return 'primary';
};

const ManagementList: React.FC = () => {
  const [rows, setRows] = useState<RegistrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState<'name' | 'location'>('name');
  const [typeFilter, setTypeFilter] = useState<RowType[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalUnfiltered, setTotalUnfiltered] = useState(0);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Advanced lawyer-only filters (ported from the Directory list)
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string[]>([]);
  const [proBonoFilter, setProBonoFilter] = useState('');
  const [freeConsultationFilter, setFreeConsultationFilter] = useState('');
  const [liabilityInsuranceFilter, setLiabilityInsuranceFilter] = useState('');
  const [minFee, setMinFee] = useState('');
  const [maxFee, setMaxFee] = useState('');
  const [practiceAreaCategories, setPracticeAreaCategories] = useState<Array<{ id: string; name: string }>>([]);

  const itemsPerPage = 10;

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        search: searchTerm,
        searchBy,
        types: typeFilter.join(','),
        includeUnverified: 'true',
        sortBy,
        sortDir,
        states: stateFilter.join(','),
        practiceAreas: practiceAreaFilter.join(','),
        proBono: proBonoFilter,
        freeConsultation: freeConsultationFilter,
        liabilityInsurance: liabilityInsuranceFilter,
        minFee,
        maxFee,
      });
      const response = await axios.get(`/api/registrations?${params}`);
      const data = response.data.registrations || [];
      const total = response.data.total ?? data.length;
      setRows(data);
      setTotalCount(total);
      setTotalUnfiltered(response.data.totalUnfiltered ?? total);
      setTotalPages(Math.ceil(total / itemsPerPage));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to fetch registrations');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTrigger, page]);

  useEffect(() => {
    const fetchPracticeAreas = async () => {
      try {
        const response = await axios.get('/api/practice-areas');
        setPracticeAreaCategories(response.data.map((cat: any) => ({ id: cat.id, name: cat.name })));
      } catch (err) {
        console.error('Error fetching practice areas:', err);
      }
    };
    fetchPracticeAreas();
  }, []);

  const triggerSearch = () => {
    setSearchTrigger((prev) => prev + 1);
    setPage(1);
  };

  const handleSort = (column: string) => {
    const isSameColumn = sortBy === column;
    const nextDir: 'asc' | 'desc' = isSameColumn && sortDir === 'asc' ? 'desc' : 'asc';
    setSortBy(column);
    setSortDir(nextDir);
    setPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const clearAllFilters = () => {
    setStateFilter([]);
    setPracticeAreaFilter([]);
    setProBonoFilter('');
    setFreeConsultationFilter('');
    setLiabilityInsuranceFilter('');
    setMinFee('');
    setMaxFee('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (stateFilter.length > 0) count++;
    if (practiceAreaFilter.length > 0) count++;
    if (proBonoFilter !== '') count++;
    if (freeConsultationFilter !== '') count++;
    if (liabilityInsuranceFilter !== '') count++;
    if (minFee) count++;
    if (maxFee) count++;
    return count;
  };

  const handleDelete = async (row: RegistrationRow) => {
    const label = row.type === 'agency' ? 'agency' : 'lawyer';
    if (!window.confirm(`Are you sure you want to delete this ${label}?`)) return;
    try {
      const endpoint = row.type === 'agency'
        ? `/api/service-providers/${row.id}`
        : `/api/lawyers/${row.id}`;
      await axios.delete(endpoint);
      fetchRegistrations();
    } catch (err: any) {
      alert('Failed to delete: ' + (err?.response?.data?.error || 'Unknown error'));
    }
  };

  const viewPath = (row: RegistrationRow) =>
    row.type === 'agency' ? `/management/agency/${row.id}` : `/management/lawyer/${row.id}`;
  const editPath = (row: RegistrationRow) =>
    row.type === 'agency' ? `/management/agency/edit/${row.id}` : `/management/edit/${row.id}`;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Registration Management
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchBy === 'location' ? 'Search by city or state...' : 'Search by name, firm, or organization...'}
            onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth>
            <InputLabel>Search By</InputLabel>
            <Select value={searchBy} label="Search By" onChange={(e) => setSearchBy(e.target.value as 'name' | 'location')}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="location">Location</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="contained" onClick={triggerSearch} startIcon={<Search />} fullWidth sx={{ height: '100%' }}>
            Search
          </Button>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Type(s)</InputLabel>
            <Select
              multiple
              value={typeFilter}
              onChange={(e) => {
                const value = e.target.value;
                setTypeFilter(typeof value === 'string' ? (value.split(',') as RowType[]) : (value as RowType[]));
              }}
              input={<OutlinedInput label="Type(s)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as RowType[]).map((value) => (
                    <Chip key={value} label={TYPE_LABELS[value]} size="small" />
                  ))}
                </Box>
              )}
            >
              {TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Search Filters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Active State(s)</InputLabel>
            <Select
              multiple
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
              }}
              input={<OutlinedInput label="State(s)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {US_STATES.map((state) => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Practice Area(s)</InputLabel>
            <Select
              multiple
              value={practiceAreaFilter}
              onChange={(e) => {
                setPracticeAreaFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
              }}
              input={<OutlinedInput label="Practice Area(s)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {practiceAreaCategories.map((category) => (
                <MenuItem key={category.id} value={category.name}>{category.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Pro Bono Interest</InputLabel>
            <Select value={proBonoFilter} label="Pro Bono Interest" onChange={(e) => setProBonoFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Free Consultation</InputLabel>
            <Select value={freeConsultationFilter} label="Free Consultation" onChange={(e) => setFreeConsultationFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Liability Insurance</InputLabel>
            <Select value={liabilityInsuranceFilter} label="Liability Insurance" onChange={(e) => setLiabilityInsuranceFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              label="Min Fee ($/hr)"
              type="number"
              value={minFee}
              onChange={(e) => setMinFee(e.target.value)}
              placeholder="0"
              size="small"
            />
            <Typography>-</Typography>
            <TextField
              label="Max Fee ($/hr)"
              type="number"
              value={maxFee}
              onChange={(e) => setMaxFee(e.target.value)}
              placeholder="1000"
              size="small"
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={clearAllFilters} disabled={getActiveFiltersCount() === 0}>
              Clear All Filters
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {getActiveFiltersCount() > 0 && `${getActiveFiltersCount()} filter(s) active`}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<Add />} onClick={() => (window.location.href = '/management/add')}>
          Add Lawyer
        </Button>
        <Button variant="outlined" startIcon={<Add />} onClick={() => (window.location.href = '/management/agency/add')}>
          Add Agency
        </Button>
        {typeFilter.length > 0 && (
          <Button variant="text" onClick={() => { setTypeFilter([]); setSearchTrigger((p) => p + 1); setPage(1); }}>
            Clear Type Filter
          </Button>
        )}
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Showing {totalCount} of {totalUnfiltered} registrations
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortBy === 'name' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'type' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'type'} direction={sortBy === 'type' ? sortDir : 'asc'} onClick={() => handleSort('type')}>
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'organization' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'organization'} direction={sortBy === 'organization' ? sortDir : 'asc'} onClick={() => handleSort('organization')}>
                    Firm / Organization
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'location' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'location'} direction={sortBy === 'location' ? sortDir : 'asc'} onClick={() => handleSort('location')}>
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell>Active States</TableCell>
                <TableCell sortDirection={sortBy === 'email' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'email'} direction={sortBy === 'email' ? sortDir : 'asc'} onClick={() => handleSort('email')}>
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'verified' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'verified'} direction={sortBy === 'verified' ? sortDir : 'asc'} onClick={() => handleSort('verified')}>
                    Verified
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'created' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'created'} direction={sortBy === 'created' ? sortDir : 'asc'} onClick={() => handleSort('created')}>
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.type}-${row.id}`}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {row.displayName}
                      {row.duplicateFlagged && (
                        <Tooltip title="Possible duplicate - flagged for review">
                          <Warning color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={TYPE_LABELS[row.type]}
                      color={typeChipColor(row.type)}
                      variant={row.type === 'referral' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell>{row.organization}</TableCell>
                  <TableCell>{[row.city, row.state].filter(Boolean).join(', ')}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{row.activeStates}</Typography>
                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={row.emailVerified ? <CheckCircle /> : <Cancel />}
                      label={row.emailVerified ? 'Verified' : 'Unverified'}
                      color={row.emailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : ''}</TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => (window.location.href = viewPath(row))}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => (window.location.href = editPath(row))}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
        </Box>
      )}

      {rows.length === 0 && !loading && (
        <Typography variant="body1" textAlign="center" sx={{ mt: 3 }}>
          No registrations found. Try adjusting your search criteria.
        </Typography>
      )}
    </Paper>
  );
};

export default ManagementList;
